//Core function for fetching data

import api from "../../utils/api/authApi";
import { getToken } from "../../utils/Functions";
import {
  ABOUT_US,
  ADD_PET,
  APPOINTMENT_BIOMETRICS,
  APPOINTMENT_DTO,
  APPOINTMENT_USER_HOMEPAGE_DTO,
  BOOKING,
  CLINIC_APPOINTMENTS,
  CLINIC_DTO,
  CLINIC_LIST,
  CLINIC_USER_DETAILS,
  CLINIC_USERS,
  CLINIC_USERS_ALL,
  CLINICS,
  CLINICS_TEMPLATES,
  CLINICS_PRESCRIPTION_TEMPLATES,
  CLINICS_REFERRAL_TEMPLATES,
  CLINICS_INSTITUTIONS,
  DOWNLOAD_IMG,
  EMPLOYEES,
  GET_WAREHOUSE,
  GET_WAREHOUSE_ITEMS,
  GET_WAREHOUSE_ITEM_AUDITING,
  WAREHOUSE_CODE_PREFIX,
  WAREHOUSE_PREFERENCES,
  INVOICE_SETTINGS,
  GET_WORKING_DAYS,
  PENDING_APPOINTMENTS,
  PENDING_APPOINTMENTS_PAGING,
  PERMISSIONS,
  PET_AVATAR,
  PET_BIOMETRICS,
  PET_USER_HOMEPAGE_DTO,
  PRICES_BASE_REQUEST,
  TEMPLATE,
  TIERS,
  EMPLOYEE_PERMISSIONS,
  USER_ROLES,
  GET_WAREHOUSE_ITEM,
  GET_APPOINTMENT_TIME_SLOTS,
  GET_INPATIENT_STAY,
  PET_MEDICAL_OVERVIEW_SUMMARY,
  GET_QR_REDIRECT,
  GET_QR_IMAGE,
  CLINICS_TEMPLATES_DROPDOWN,
} from "../ApiPaths";
import { PET_DETAILS_ } from "../UrlPaths";
import dayjs from "dayjs";

export async function infiniteFetch(urlPath, id, pageParam) {
  const token = getToken();
  if (token == null || urlPath == null) {
    return null;
  }
  try {
    const requestId = id;
    const tag = null;
    const path = requestId ? urlPath + requestId : urlPath;
    const res = await api.get(path, {
      params: {
        tag: tag,
        pageParam: pageParam,
      },
    });
    return res?.data;
  } catch (error) {
    throw error;
  }
}

export async function fetch(urlPath, id) {
  const token = getToken();
  if (token == null || urlPath == null) {
    return null;
  }
  const path = id ? urlPath + id : urlPath;
  try {
    const res = await api.get(path, {
      params: { tag: null },
    });
    return res?.data ?? null;
  } catch (error) {
    throw error;
  }
}

export async function fetchNoBearer(url) {
  try {
    const res = await api.get(url);
    return res?.data;
  } catch (error) {
    throw error;
  }
}

export async function fetchClinicList() {
  return await fetchNoBearer(CLINIC_LIST);
}

export async function fetchClinicsMaster() {
  return await fetch(CLINICS);
}

export async function fetchClinic(clinicId) {
  const url = CLINIC_DTO + clinicId;
  return await fetch(url);
}

export async function fetchAppointments(clinicId, userId, adminOrEmployee) {
  const userPrivilage = adminOrEmployee;
  const API_PATH = userPrivilage ? CLINIC_APPOINTMENTS : BOOKING;
  const API_ID = userPrivilage ? clinicId : userId;
  const res = await fetch(API_PATH, API_ID);
  return res;
}

export async function fetchPendingAppointments() {
  const url = PENDING_APPOINTMENTS_PAGING;
  const res = await fetch(url);
  return res;
}

export async function fetchAppointment(id, tag) {
  const url = APPOINTMENT_DTO + id + "?tag=" + tag;
  return await fetch(url);
}

export async function fetchPetsTab(tab, petId) {
  const url = PET_DETAILS_ + petId + "?tab=" + tab;
  const res = await fetch(url);
  return res;
}

export async function fetchPet(user_id) {
  const url = ADD_PET;
  return await fetch(url, user_id);
}

export async function fetchWorkingDays(clinicId) {
  const path = GET_WORKING_DAYS;
  const res = await fetch(path, clinicId);
  return res;
}

export async function fetchTemplates(clinicId, title, category) {
  const params = new URLSearchParams();

  if (title && title.trim() !== "") {
    params.append("title", title);
  }

  if (category) {
    params.append("category", category);
  }

  const paramString = params.toString();
  const url = `${CLINICS_TEMPLATES}${clinicId}${paramString ? `?${paramString}` : ""}`;
  return await fetch(url);
}

export async function fetchPrescriptionTemplates(clinicId, title) {
  const params = new URLSearchParams();

  if (title && title.trim() !== "") {
    params.append("title", title);
  }

  const paramString = params.toString();
  const url = `${CLINICS_PRESCRIPTION_TEMPLATES}${clinicId}${paramString ? `?${paramString}` : ""}`;
  return await fetch(url);
}

export async function fetchReferralTemplates(clinicId, referralType, title) {
  const params = new URLSearchParams();

  if (referralType) {
    params.append("type", referralType);
  }

  if (title && title.trim() !== "") {
    params.append("title", title);
  }

  const paramString = params.toString();
  const url = `${CLINICS_REFERRAL_TEMPLATES}${clinicId}${paramString ? `?${paramString}` : ""}`;
  return await fetch(url);
}

export async function fetchInstitutions(clinicId, name) {
  const params = new URLSearchParams();

  if (name && name.trim() !== "") {
    params.append("name", name);
  }

  const paramString = params.toString();
  const url = `${CLINICS_INSTITUTIONS}${clinicId}${paramString ? `?${paramString}` : ""}`;
  return await fetch(url);
}

export async function fetchTemplate(id) {
  const url = TEMPLATE;
  return fetch(url, id);
}

export async function fetchDropdownTemplates(clinicId, category) {
  const params = new URLSearchParams();

  if (category) {
    params.append("category", category);
  }

  const paramString = params.toString();
  const url = `${CLINICS_TEMPLATES_DROPDOWN}${clinicId}${paramString ? `?${paramString}` : ""}`;
  return await fetch(url);
}

export async function fetchUser() {}

export async function fetchAllSpecies(isMaster) {
  if (isMaster) {
    const url = "/species/";
    return fetch(url);
  }
}

export async function fetchAboutUs(clinciId) {
  const url = ABOUT_US;
  return fetch(url, clinciId);
}

export async function fetchBanner(clinicId) {
  const fileType = "ABOUT_US";
  if (!clinicId) return;
  const url = `${DOWNLOAD_IMG}${clinicId}/${fileType}`;

  return fetch(url);
}

export async function fetchLogo(clinicId, isMaster) {
  const fileType = "LOGO";
  if (!clinicId) return;
  const url = `${DOWNLOAD_IMG}${clinicId}/${fileType}`;
  if (!isMaster) {
    return await fetch(url);
  }
  return null;
}

export async function fetchSingleClinicUser(userId) {
  const url = "/users/details/";
  return await fetch(url, userId);
  //Create endpoint for fetching single user details, return profile dto
}

export async function fetchClinicUsers(url) {
  return await fetch(url);
}

export async function fetchUsersTable(
  page,
  size,
  q,
  sort,
  clinicId,
  active,
  withoutPets,
  withoutReservations,
) {
  const params = new URLSearchParams({
    pageNumber: page,
    size,
    q: q || "",
    sort: sort || "",
  });
  if (clinicId != null) {
    params.append("clinicId", clinicId);
  }
  if (active != null) {
    params.append("active", active);
  }
  if (withoutPets != null) {
    params.append("withoutPets", withoutPets);
  }
  if (withoutReservations != null) {
    params.append("withoutReservations", withoutReservations);
  }

  const urlPath = `/users/clinic/paging?${params}`;
  const response = await fetch(urlPath);

  return response;
}

export async function fetchEmployee(id) {
  const url = EMPLOYEES;
  return await fetch(url, id);
}

export async function fetchServices(id) {
  const url = PRICES_BASE_REQUEST;
  return await fetch(url, id);
}

export async function fetchAppointmentBiometrics(id, tag) {
  const url = APPOINTMENT_BIOMETRICS + id;
  return await fetch(url);
}

export async function fetchPetBiometrics(id, tag) {
  const url = PET_BIOMETRICS + id;
  return await fetch(url);
}

export async function fetchUserHomePagePets(id) {
  const url = PET_USER_HOMEPAGE_DTO + id;
  return await fetch(url);
}

export async function fetchUserHomePageAppointments() {
  const url = APPOINTMENT_USER_HOMEPAGE_DTO;
  return await fetch(url);
}

export async function fetchPetAvatar(id) {
  const url = ADD_PET + PET_AVATAR + id;
  return await fetch(url);
}

export async function fetchPermissionsForEmployee(id) {
  const url = EMPLOYEE_PERMISSIONS + id;
  return await fetch(url);
}

export async function fetchTiers() {
  const url = TIERS;
  return await fetch(url);
}

export async function fetchRoleByUserId(id) {
  const url = USER_ROLES + id;
  return await fetch(url);
}

export async function fetchWarehouse(id) {
  const url = GET_WAREHOUSE + id;
  return await fetch(url);
}

export async function fetchWarehouseItems(clinicId, { categoryId, supplierId, search, sort, page, size } = {}) {
  const params = new URLSearchParams();
  if (categoryId) {
    params.append("categoryId", categoryId);
  }
  if (supplierId) {
    params.append("supplierId", supplierId);
  }
  if (search && search.trim() !== "") {
    params.append("search", search.trim());
  }
  if (sort) {
    params.append("sort", sort);
  }
  if (page !== null && page !== undefined) {
    params.append("page", page);
  }
  if (size !== null && size !== undefined) {
    params.append("size", size);
  }

  const paramString = params.toString();
  const url = `${GET_WAREHOUSE_ITEMS}${clinicId}${paramString ? `?${paramString}` : ""}`;
  return await fetch(url);
}

export async function fetchWarehouseItem(itemId) {
  return await fetch(GET_WAREHOUSE_ITEM, itemId);
}

export async function fetchWarehouseCodePrefix(clinicId) {
  return await fetch(WAREHOUSE_CODE_PREFIX, clinicId);
}

export async function fetchWarehousePreferences(clinicId) {
  return await fetch(WAREHOUSE_PREFERENCES, clinicId);
}

export async function fetchInvoiceSettings(clinicId) {
  return await fetch(INVOICE_SETTINGS, clinicId);
}

export async function fetchWarehouseItemAuditing(itemId) {
  const url = `${GET_WAREHOUSE_ITEM_AUDITING}${itemId}/auditing`;
  return await fetch(url);
}

export async function fetchAvailableTimeSlots(serviceId, date) {
  const formattedDate = date ? dayjs(date).format("DD/MM/YYYY") : null;
  const params = new URLSearchParams();
  if (date) {
    params.append("date", formattedDate);
  }
  if (serviceId) {
    params.append("serviceId", serviceId);
  }
  const url = GET_APPOINTMENT_TIME_SLOTS + params.toString();
  return await fetch(url);
}

export async function fetchInpatientStayByPet(url) {
  return await fetch(url);
}

export async function fetchPetMedicalOverview(petId) {
  const url = PET_MEDICAL_OVERVIEW_SUMMARY + petId;
  return await fetch(url);
}

export async function fetchQrRedirect(id) {
  const url = GET_QR_REDIRECT + id;
  return await fetchNoBearer(url);
}

export async function fetchQrCodeSvg(clinicId) {
  if (!clinicId) {
    return Promise.reject(new Error("Clinic ID is required."));
  }

  const url = `${GET_QR_IMAGE}${clinicId}`;

  try {
    const response = await api.get(url, {
      responseType: "blob",
    });

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read the SVG blob."));
      reader.readAsDataURL(response.data);
    });
  } catch (error) {
    throw error;
  }
}

export async function fetchWarehouseItemQrSvg(itemId) {
  if (!itemId) {
    return Promise.reject(new Error("Item ID is required."));
  }

  const url = `${GET_WAREHOUSE_ITEM}${itemId}/qr`;

  const response = await api.get(url, {
    responseType: "blob",
  });

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read the SVG blob."));
    reader.readAsDataURL(response.data);
  });
}
